import { useEffect, useState } from "react";
import { X, GraduationCap, User as UserIcon, Calendar, BookOpen, Paperclip, ExternalLink } from "lucide-react";
import { resolveAttachmentUrl } from "@/lib/api";
import { PdfViewerModal } from "@/components/PdfViewerModal";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T"));
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("sv-SE", { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
};

/**
 * Städar Classroom-text där HTML-parsning lämnat dåligt formaterade
 * punktlistor. Exempel: "läxor:\n-\nDialekter: ..." → "läxor:\n- Dialekter: ..."
 *
 * - Slår ihop ensamma "-"-rader med nästa rad
 * - Komprimerar 3+ tomma rader till max 2
 * - Trimmar trailing whitespace per rad
 */
function cleanDescription(s) {
  if (!s || typeof s !== "string") return s || "";
  let out = s.replace(/\r\n/g, "\n");
  // "punkt på en rad, innehåll på nästa" → samma rad
  out = out.replace(/(^|\n)([-•·*])\s*\n+\s*/g, "$1$2 ");
  // Trimma trailing whitespace per rad
  out = out
    .split("\n")
    .map((line) => line.replace(/\s+$/g, ""))
    .join("\n");
  // 3+ tomma rader → max 2
  out = out.replace(/\n{3,}/g, "\n\n");
  return out.trim();
}

/**
 * VklassDetailSheet — visar Vklass- eller Classroom-poster in-app utan att
 * länka ut till externa portaler. Anpassar etikett, färg och bilage-stöd
 * efter `item.source` ("vklass" eller "classroom"/"varmdo").
 *
 * Props:
 *   item: feed-item ({title, subtitle, description, attachments, link, ...})
 *   onClose: stäng-callback
 */
export function VklassDetailSheet({ item, onClose }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!item) return null;

  // Källspecifika labels — Classroom + Värmdö delar samma "Classroom"-etikett
  const isClassroom = item.source === "classroom" || item.source === "varmdo";
  const sourceLabel = isClassroom ? "Classroom" : "Vklass";
  const sourceAccentClass = isClassroom
    ? "bg-[#EEF2F7] text-[color:var(--jarvis)]"
    : "bg-[#F0F6E6] text-[color:var(--moss)]";
  const sourceTextClass = isClassroom
    ? "text-[color:var(--jarvis)]"
    : "text-[color:var(--moss)]";

  // subtitle = "Anton · Engelska Åk 6" → splitta
  const subParts = (item.subtitle || "").split("·").map((s) => s.trim()).filter(Boolean);
  const owner = item.author || subParts[0] || "";
  const course = subParts.length > 1 ? subParts.slice(1).join(" · ") : "";

  const attachments = Array.isArray(item.attachments) ? item.attachments : [];

  // Bild-sidor för bläddring (svep) i visaren + "bok"-PDF
  const isImageAtt = (att) => {
    const mime = (att.mime || "").toLowerCase();
    const t = (att.title || "").toLowerCase();
    return mime.startsWith("image/") || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(t);
  };
  const imagePages = attachments
    .filter((att) => isImageAtt(att) && (att.proxy_url || (att.proxied && att.url)))
    .map((att) => ({
      url: resolveAttachmentUrl(att.proxy_url || att.url),
      title: att.title || "Bild",
      mime: att.mime || "image/png",
    }));
  const streamItemId =
    typeof item.id === "string" && item.id.startsWith("varmdo_stream:")
      ? item.id.split(":")[1]
      : item.stream_item_id || null;
  const bookUrl =
    streamItemId && imagePages.length >= 2
      ? resolveAttachmentUrl(`/api/varmdo/stream-item/${streamItemId}/book.pdf`)
      : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-6"
      onClick={onClose}
      data-testid="vklass-detail-backdrop"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl h-[92dvh] sm:h-auto sm:max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="vklass-detail-sheet"
      >
        <header className="px-5 py-4 border-b border-[color:var(--border)] flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl ${sourceAccentClass} flex items-center justify-center flex-shrink-0`}>
            <GraduationCap size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${sourceTextClass}`}>
              {sourceLabel}
              {item.kind && item.kind !== "announcement" && (
                <span className="text-[color:var(--text-tertiary)] font-normal ml-2 normal-case tracking-normal">
                  · {item.kind === "assignment" ? "Uppgift" : item.kind === "material" ? "Material" : item.kind === "question" ? "Fråga" : item.kind}
                </span>
              )}
            </p>
            <h2 className="font-display text-lg font-medium leading-snug mt-0.5">
              {item.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-[color:var(--bg-elevated)] -mr-1"
            aria-label="Stäng"
            data-testid="vklass-detail-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4" data-testid="vklass-detail-body">
          <div className="grid grid-cols-1 gap-2.5 text-sm">
            {owner && (
              <div className="flex items-center gap-2 text-[color:var(--text-secondary)]">
                <UserIcon size={14} className="text-[color:var(--text-tertiary)]" />
                <span className="font-medium text-[color:var(--text-primary)]">{owner}</span>
              </div>
            )}
            {course && (
              <div className="flex items-center gap-2 text-[color:var(--text-secondary)]">
                <BookOpen size={14} className="text-[color:var(--text-tertiary)]" />
                <span>{course}</span>
              </div>
            )}
            {(item.event_start || item.date_text || item.timestamp) && (
              <div className="flex items-center gap-2 text-[color:var(--text-secondary)]">
                <Calendar size={14} className="text-[color:var(--text-tertiary)]" />
                <span>{item.date_text || fmtDate(item.event_start || item.timestamp)}</span>
              </div>
            )}
          </div>

          {item.description ? (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5">
                {isClassroom ? "Inlägg från läraren" : "Lärarens beskrivning"}
              </p>
              <p className="text-sm whitespace-pre-line leading-relaxed text-[color:var(--text-primary)] break-words">
                {cleanDescription(item.description)}
              </p>
            </div>
          ) : (
            <p className="text-sm text-[color:var(--text-tertiary)] italic">
              Ingen ytterligare beskrivning från läraren än.
            </p>
          )}

          {attachments.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
                Bilagor · {attachments.length}
              </p>
              {bookUrl && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview({
                      url: bookUrl,
                      title: `${item.title || "Material"} — alla sidor`,
                      mime: "application/pdf",
                    });
                  }}
                  className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[color:var(--jarvis)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
                  data-testid="classroom-open-book-btn"
                >
                  <BookOpen size={14} /> Öppna alla {imagePages.length} sidor som bok (PDF)
                </button>
              )}
              <ul className="space-y-1.5" data-testid="classroom-attachments">
                {attachments.map((att, i) => {
                  const proxyPath = att.proxy_url || (att.proxied ? att.url : null);
                  const isProxied = !!proxyPath;
                  const title = att.title || att.url || `Bilaga ${i + 1}`;
                  const rawUrl = (att.url || "").toLowerCase();
                  const isGoogleDoc = /docs\.google\.com\/(document|spreadsheets|presentation)\//.test(rawUrl);
                  const mimeOk =
                    typeof att.mime === "string" &&
                    (att.mime.startsWith("application/pdf") || att.mime.startsWith("image/"));
                  const extOk = /\.(pdf|png|jpe?g|gif|webp)(\?|$)/i.test(title);
                  const canPreview = isProxied && (mimeOk || extOk || isGoogleDoc);
                  const href = resolveAttachmentUrl(proxyPath || att.url);
                  return (
                    <li key={`att-${i}-${title.slice(0, 30)}`}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canPreview && href) {
                            const isImg = isImageAtt(att);
                            const startIndex = isImg
                              ? Math.max(0, imagePages.findIndex((p) => p.title === (att.title || "Bild")))
                              : 0;
                            setPreview({
                              url: href,
                              title,
                              mime: isGoogleDoc ? "application/pdf" : att.mime,
                              pages: isImg && imagePages.length > 1 ? imagePages : undefined,
                              startIndex,
                            });
                          } else if (href) {
                            window.open(href, "_blank", "noopener,noreferrer");
                          }
                        }}
                        className="w-full flex items-center gap-2 text-left px-2.5 py-2 rounded-lg border border-[color:var(--border)] hover:bg-[color:var(--bg-elevated)] transition-colors"
                        data-testid={`classroom-attachment-${i}`}
                      >
                        <Paperclip size={13} className="text-[color:var(--text-tertiary)] flex-shrink-0" />
                        <span className="text-sm truncate flex-1">{title}</span>
                        {canPreview ? (
                          <span className="text-[10px] font-medium text-[color:var(--jarvis)] flex-shrink-0">
                            Visa
                          </span>
                        ) : (
                          <ExternalLink size={12} className="text-[color:var(--text-tertiary)] flex-shrink-0" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {preview && (
        <PdfViewerModal
          url={preview.url}
          title={preview.title}
          mime={preview.mime}
          pages={preview.pages}
          startIndex={preview.startIndex || 0}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

export default VklassDetailSheet;
